import { GetScheduleForContactRequest, IcmContactSchedule, useORPCQuery } from "Core/oRPC";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import { useRootStore } from "Store/RootStore";
import { utcDateTime } from "../../Shared/Hooks/Time/useTimeZone";

export const useUpdateCurrentUserOCERole = () => {
    const myStore = useRootStore("MyStore");
    const { oncallApi } = useORPCQuery();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const { data: contact } = useQuery(
        oncallApi.getContactv2.queryOptions({
            input: { alias: myStore.profile.Alias ?? "" },
            staleTime: Infinity,
            enabled: !!myStore.profile.Alias
        })
    );

    const onCallScheduleRequest = useMemo((): GetScheduleForContactRequest => {
        const today = DateTime.now().startOf("day");
        const request: GetScheduleForContactRequest = {
            TeamIds: [],
            StartTime: today.toISO(),
            EndTime: today.plus({ day: 1 }).toISO(),
            OnCallPositionRange: [0, 2],
            ScheduleType: ["NoSubstitution", "SubstituteForContact"]
        };

        return request;
    }, []);

    const isOCERoleExpired = useMemo(() => utcDateTime(myStore.userOCERoleExpirationTime ?? "") < utcDateTime(), [myStore.userOCERoleExpirationTime]);

    const {
        data: contactOncallSchedule,
        status: onCallLoadingStatus,
        refetch
    } = useQuery(
        oncallApi.postTeamsv2GetOnCallScheduleForContact.queryOptions({
            input: { body: onCallScheduleRequest, query: { contactId: contact?.Id ? parseInt(contact.Id, 10) : undefined } },
            staleTime: 30 * 60 * 1000,
            enabled: !!contact?.Id && isOCERoleExpired
        })
    );

    const updateOCERoleInStore = useCallback(
        (oncallSchedule: IcmContactSchedule | undefined) => {
            if (oncallSchedule?.ContactOnCallSlots) {
                const now = utcDateTime();
                const currentOCEShifts = oncallSchedule.ContactOnCallSlots.filter(
                    (shift) => utcDateTime(shift.StartTime ?? "") <= now && utcDateTime(shift.EndTime ?? "") >= now
                );
                const primaryShift = currentOCEShifts.find((shift) => shift.OnCallPosition === 0);
                const secondaryShift = currentOCEShifts.find((shift) => shift.OnCallPosition === 1);
                const defaultExpirationTime = now.plus({ day: 1 }).toISO();
                if (primaryShift) {
                    myStore.userOCERole = "primary";
                    myStore.userOCERoleExpirationTime = primaryShift.EndTime ?? defaultExpirationTime;
                } else if (secondaryShift) {
                    myStore.userOCERole = "secondary";
                    myStore.userOCERoleExpirationTime = secondaryShift.EndTime ?? defaultExpirationTime;
                } else {
                    myStore.userOCERole = "none";
                    myStore.userOCERoleExpirationTime = defaultExpirationTime;
                }
            }
        },
        [myStore]
    );

    useEffect(() => {
        if (onCallLoadingStatus === "success" && isOCERoleExpired) {
            updateOCERoleInStore(contactOncallSchedule);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onCallLoadingStatus, isOCERoleExpired]);

    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(
            async () => {
                if (utcDateTime(myStore.userOCERoleExpirationTime ?? "") < utcDateTime()) {
                    const result = await refetch();

                    if (result.data && result.isFetched) {
                        updateOCERoleInStore(result.data);
                    }
                }
            },
            60 * 60 * 1000
        );

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [myStore, refetch, updateOCERoleInStore]);
};
